import axios from 'axios';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import parseISO from 'date-fns/parseISO';
import subSeconds from 'date-fns/subSeconds';
import queryString from 'query-string';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import urlJoin from 'url-join';

import { useConfig } from './config';
import useActionNotifier from './use-action-notifier';
import useInterval from './use-interval';

export interface Session {
  email: string;
  formType: string;
}

export function useCheckSession() {
  const [session, setSession] = useState<Session>({} as Session);
  const [hasSession, setHasSession] = useState(false);
  const [loading, setLoading] = useState(true);
  const [continued, setContinued] = useState(false);
  const [isExpiring, setIsExpiring] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const { api, idleWarningSecs = 60, sessionPollSecs = 60 } = useConfig();
  const navigate = useNavigate();
  const sessionUrl = urlJoin(api, 'session');

  useActionNotifier();
  useEffect(() => {
    if (hasSession && isExpired) {
      navigate('/expired');
    }
  }, [hasSession, isExpired, navigate]);

  // Session ID provided by link from email, otherwise api
  // uses previously set cookie.
  const { search } = useLocation();
  const { sessionId } = queryString.parse(search);

  // validate request
  useEffect(() => {
    const checkData = sessionId ? { sessionId } : null;
    const getData = async () => {
      const response = await axios.post(sessionUrl, checkData, {
        withCredentials: true,
        validateStatus: () => true,
      });
      const { status, data } = response;
      if (status === 200) {
        setHasSession(true);
        setSession(data);
        setLoading(false);
      } else {
        if (data?.retries && data?.error) {
          navigate('/closed-session', { state: { error: data?.error } });
        } else {
          const path = `/expired${sessionId ? '?invalidUrl=true' : ''}`;
          navigate(path);
        }
      }
    };
    getData();
  }, [sessionId, navigate, sessionUrl, setSession, setLoading]);

  useInterval(async () => {
    try {
      const { data } = await axios.get(sessionUrl, { withCredentials: true });
      const expiresAtRaw = data?.expiresAt;
      const newExpiresAt = expiresAtRaw ? parseISO(expiresAtRaw) : null;
      setExpiresAt(newExpiresAt);

      const now = new Date();
      const expires = newExpiresAt && subSeconds(newExpiresAt, sessionPollSecs);

      const expired = !data || !expires || expires < now;
      const expiring = !!(
        data &&
        expires &&
        differenceInSeconds(expires, now) <= idleWarningSecs
      );

      setIsExpired(expired);
      setIsExpiring(expiring);

      setContinued(false);
    } catch {
      setIsExpired(true);
      setIsExpiring(false);
    }
  }, sessionPollSecs * 1000);

  const extend = async () => {
    try {
      const { data } = await axios.post(sessionUrl, null, {
        withCredentials: true,
      });
      const sessionActive = !!data;
      setIsExpired(!sessionActive);
      setIsExpiring(!sessionActive);
    } catch {
      setIsExpired(true);
      setIsExpiring(false);
    }
  };

  return {
    sessionId: sessionId as string,
    session,
    loading,
    continued,
    isExpiring,
    expiresAt,
    extend: async () => {
      setContinued(true);
      await extend();
    },
  };
}
